import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { resolveImageUrl } from '../lib/api';

interface Props {
  url: string;
  alt: string;
  onClose: () => void;
}

/**
 * Full-size view of a single image, over everything else.
 *
 * Exists because the product form only ever showed thumbnails — a 56px source
 * photo is not enough to write a garment description from, which is the one
 * thing the person filling the catalogue has to do while looking at it.
 *
 * Sits above the product form (z-50) and the AI modal (z-60), so it is
 * usable from either.
 */
export function ImageLightbox({ url, alt, onClose }: Props) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Escape closes. Captured on the document because the click target may be
    // the image itself, which takes no focus.
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Stop the key reaching the form modal underneath, which would close
        // that too and lose whatever was being typed.
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey, true);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label={t('adminProducts.image.zoomClose')}
        className="absolute top-4 end-4 rounded-full bg-white/90 hover:bg-white p-2 text-gray-800 shadow"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={resolveImageUrl(url)}
        alt={alt}
        // Clicking the image must not close: the backdrop handler would fire
        // on every attempt to look closely at a detail.
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full object-contain rounded shadow-2xl"
      />
    </div>
  );
}
